/**
 * Parse API instants (stored as UTC) and show them in the user's local timezone.
 *
 * Backend sends full datetimes as ISO-8601 with a ``Z`` suffix. Strings without
 * a zone that look like ``YYYY-MM-DDTHH:mm`` are treated as UTC (legacy / defensive).
 */

function hasExplicitOffset(iso: string): boolean {
  return /[zZ]$/.test(iso) || /[+-]\d{2}:\d{2}$/.test(iso) || /[+-]\d{4}$/.test(iso)
}

/** Parse a UTC instant from the API into a Date (browser-local getters reflect the user's zone). */
export function parseApiUtcInstant(iso: string | null | undefined): Date | null {
  if (iso == null) return null
  const t = String(iso).trim()
  if (!t) return null
  if (/^\d{4}-\d{2}-\d{2}T/.test(t) && !hasExplicitOffset(t)) {
    return new Date(t + "Z")
  }
  return new Date(t)
}

/** Format a UTC API string for display in the user's locale and local timezone. */
export function formatApiInstantInUserLocale(
  iso: string | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    dateStyle: "medium",
    timeStyle: "short",
  }
): string {
  const d = parseApiUtcInstant(iso)
  if (!d || Number.isNaN(d.getTime())) {
    return iso?.trim() ? String(iso) : "—"
  }
  return d.toLocaleString(undefined, options)
}

/** Split a UTC API instant into separate date and time lines for compact tables. */
export function formatApiInstantDateTimeLines(
  iso: string | null | undefined,
  options: {
    date?: Intl.DateTimeFormatOptions
    time?: Intl.DateTimeFormatOptions
  } = {}
): { dateLine: string; timeLine: string } | null {
  const d = parseApiUtcInstant(iso)
  if (!d || Number.isNaN(d.getTime())) return null
  const dateLine = d.toLocaleDateString(
    undefined,
    options.date ?? { dateStyle: "medium" }
  )
  const timeLine = d.toLocaleTimeString(
    undefined,
    options.time ?? { timeStyle: "short" }
  )
  return { dateLine, timeLine }
}

function calendarYearInZone(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric" }).format(date)
}

/**
 * Calendar date for a UTC API instant in an IANA timezone.
 * Omits the year when it matches ``now`` in that zone, and never includes a time.
 */
export function formatWorkspaceCalendarDate(
  iso: string | null | undefined,
  timeZone: string,
  now: Date = new Date(),
): string {
  if (iso == null) return ""
  const s = String(iso).trim()
  if (!s) return ""
  const d = parseApiUtcInstant(s)
  if (!d || Number.isNaN(d.getTime())) return s
  const zone = timeZone.trim() || "UTC"
  let sameYear = false
  try {
    sameYear = calendarYearInZone(d, zone) === calendarYearInZone(now, zone)
  } catch {
    return d.toLocaleDateString(undefined, { timeZone: "UTC", month: "short", day: "numeric", year: "numeric" })
  }
  const options: Intl.DateTimeFormatOptions = sameYear
    ? { timeZone: zone, month: "short", day: "numeric" }
    : { timeZone: zone, month: "short", day: "numeric", year: "numeric" }
  return d.toLocaleDateString(undefined, options)
}

/** Contacts/leads/lists tables: local date/time; omit year when it matches the current year. */
export function formatContactListInstant(
  iso: string | null | undefined,
  now: Date = new Date()
): string {
  if (iso == null) return ""
  const s = String(iso).trim()
  if (!s) return ""
  const d = parseApiUtcInstant(s)
  if (!d || Number.isNaN(d.getTime())) {
    return s
  }
  const sameYear = d.getFullYear() === now.getFullYear()
  const options: Intl.DateTimeFormatOptions = sameYear
    ? { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }
    : { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }
  return d.toLocaleString(undefined, options)
}

